import { useEffect, useState } from 'react';
import type { PublicUser, ThreadAcl, UserRole } from '../types/gateway';
import styles from './ShareModal.module.css';

interface ShareModalProps {
  thread: ThreadAcl;
  roster: PublicUser[];
  /** Optional invite-link generator — owner of thread only. Returns full URL. */
  onCreateInviteLink?: (sessionKey: string, expiresHours: number) => Promise<string>;
  onSave: (sharedWith: string[] | '*') => Promise<void>;
  onClose: () => void;
}

const ROLE_LABELS: Record<UserRole, string> = {
  designer: 'Designers',
  field: 'Field crew',
  accounting: 'Accounting / Office',
  operations: 'Operations',
  admin: 'Admins',
};

export function ShareModal({ thread, roster, onCreateInviteLink, onSave, onClose }: ShareModalProps) {
  const [orgWide, setOrgWide] = useState(thread.sharedWith === '*');
  const [selected, setSelected] = useState<Set<string>>(
    new Set(Array.isArray(thread.sharedWith) ? thread.sharedWith : [])
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [linkExpiry, setLinkExpiry] = useState(168); // hours; 7 days default
  const [linkUrl, setLinkUrl] = useState<string | null>(null);
  const [linkLoading, setLinkLoading] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  // Roster minus the owner (you can't "share with yourself")
  const others = roster.filter((u) => u.username !== thread.owner);

  // Group by uiRole for the role-quick-select section.
  const byRole: Partial<Record<UserRole, PublicUser[]>> = {};
  for (const u of others) {
    const role = u.uiRole as UserRole;
    if (!byRole[role]) byRole[role] = [];
    byRole[role]!.push(u);
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const toggle = (username: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(username)) next.delete(username); else next.add(username);
      return next;
    });
  };

  const toggleRole = (role: UserRole) => {
    const usernames = (byRole[role] || []).map((u) => u.username);
    const allSelected = usernames.every((u) => selected.has(u));
    setSelected((prev) => {
      const next = new Set(prev);
      for (const u of usernames) {
        if (allSelected) next.delete(u);
        else next.add(u);
      }
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setErr(null);
    try {
      await onSave(orgWide ? '*' : Array.from(selected));
      onClose();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateLink = async () => {
    if (!onCreateInviteLink) return;
    setLinkLoading(true);
    setErr(null);
    try {
      const url = await onCreateInviteLink(thread.sessionKey, linkExpiry);
      setLinkUrl(url);
      setLinkCopied(false);
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'Could not create link');
    } finally {
      setLinkLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (!linkUrl) return;
    try {
      await navigator.clipboard.writeText(linkUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className={styles.header}>
          <h2 className={styles.title}>Share &ldquo;{thread.name || thread.sessionKey}&rdquo;</h2>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Close">{'\u2715'}</button>
        </div>

        <div className={styles.body}>
          <label className={styles.orgRow}>
            <input
              type="checkbox"
              checked={orgWide}
              onChange={(e) => setOrgWide(e.target.checked)}
            />
            <span>
              <strong>Share with the whole team</strong>
              <span className={styles.hint}>Everyone with an account sees this thread.</span>
            </span>
          </label>

          <div className={`${styles.section} ${orgWide ? styles.disabled : ''}`}>
            <div className={styles.sectionLabel}>Quick add by role</div>
            <div className={styles.roleChips}>
              {(Object.keys(byRole) as UserRole[]).map((role) => {
                const usernames = (byRole[role] || []).map((u) => u.username);
                const allSelected = usernames.length > 0 && usernames.every((u) => selected.has(u));
                return (
                  <button
                    key={role}
                    type="button"
                    className={`${styles.roleChip} ${allSelected ? styles.roleChipActive : ''}`}
                    onClick={() => toggleRole(role)}
                    disabled={orgWide}
                  >
                    {ROLE_LABELS[role] || role}
                    <span className={styles.roleChipCount}>{usernames.length}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className={`${styles.section} ${orgWide ? styles.disabled : ''}`}>
            <div className={styles.sectionLabel}>People</div>
            <div className={styles.userList}>
              {others.map((u) => {
                const checked = selected.has(u.username);
                return (
                  <label key={u.username} className={`${styles.userRow} ${checked ? styles.userRowOn : ''}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(u.username)}
                      disabled={orgWide}
                    />
                    <span className={styles.userMeta}>
                      <span className={styles.userName}>{u.name}</span>
                      <span className={styles.userSub}>
                        @{u.username}{u.email ? ` · ${u.email}` : ''}
                      </span>
                    </span>
                    <span className={styles.userRole}>{u.uiRole}</span>
                  </label>
                );
              })}
              {others.length === 0 && (
                <div className={styles.empty}>No other team members yet.</div>
              )}
            </div>
          </div>

          {onCreateInviteLink && (
            <div className={styles.section}>
              <div className={styles.sectionLabel}>Magic link</div>
              <div className={styles.linkRow}>
                <select
                  className={styles.expirySelect}
                  value={linkExpiry}
                  onChange={(e) => { setLinkExpiry(Number(e.target.value)); setLinkUrl(null); }}
                  disabled={linkLoading}
                >
                  <option value={24}>Expires in 24 hours</option>
                  <option value={168}>Expires in 7 days</option>
                  <option value={720}>Expires in 30 days</option>
                </select>
                {!linkUrl ? (
                  <button
                    type="button"
                    className={styles.linkBtn}
                    onClick={handleCreateLink}
                    disabled={linkLoading}
                  >
                    {linkLoading ? 'Creating…' : 'Create invite link'}
                  </button>
                ) : (
                  <button
                    type="button"
                    className={styles.linkBtn}
                    onClick={handleCopyLink}
                  >
                    {linkCopied ? 'Copied!' : 'Copy link'}
                  </button>
                )}
              </div>
              {linkUrl && (
                <div className={styles.linkUrl}>{linkUrl}</div>
              )}
            </div>
          )}

          {err && <div className={styles.err}>{err}</div>}
        </div>

        <div className={styles.footer}>
          <button className={styles.cancelBtn} onClick={onClose} disabled={saving}>Cancel</button>
          <button className={styles.saveBtn} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save sharing'}
          </button>
        </div>
      </div>
    </div>
  );
}
